import React, { useState, useRef, useEffect, useCallback, useMemo, useLayoutEffect } from 'react';
import { Table, Input, Button, Space, Row, Col, Card, Breadcrumb, Badge, Select, Divider, notification, Empty, Alert, Dropdown, Form, theme } from 'antd';
import { PlusOutlined, HomeOutlined, SearchOutlined, EyeOutlined, EditOutlined, DownOutlined, FileExcelOutlined } from '@ant-design/icons';
import * as XLSX from "xlsx";
import Create from './Create';
import { Resizable } from 'react-resizable';
import { useOutletContext } from 'react-router-dom';
import TradingPartnerAPI from '../../../restAPI/TradingPartnersAPI';
import { useSelector } from 'react-redux';
import Edit from './Edit';
import View from './View';

const ResizableTitle = (props) => {
  const { onResize, width, children, ...restProps } = props;
  if (!width) return <th {...restProps}>{children}</th>;

  const handleResize = (e, { size }) => {
    // Prevent ResizeObserver error with debounce
    if (onResize) {
      try {
        // Use setTimeout to debounce the resize operation
        setTimeout(() => {
          try {
            onResize(size.width);
          } catch (error) {
            // Silently handle ResizeObserver errors
            if (error.message && error.message.includes('ResizeObserver')) {
              return;
            }
            throw error;
          }
        }, 0);
      } catch (error) {
        // Silently handle ResizeObserver errors
        if (error.message && error.message.includes('ResizeObserver')) {
          return;
        }
        throw error;
      }
    }
  };

  return (
    <th {...restProps}>
      <Resizable
        width={width}
        height={0}
        onResize={handleResize}
        draggableOpts={{ enableUserSelectHack: false }}
        handle={
          <span
            style={{
              position: "absolute",
              right: 0,
              bottom: 0,
              cursor: "col-resize",
              width: "2px",
              height: "100%",
              background: "#ffeb3b3b",
            }}
          />
        }
      >
        <div>{children}</div>
      </Resizable>
    </th>
  );
};

const onSearch = (value) => {
  console.log("search:", value);
};

export default function CompanyTable() {
  const {
    token: { colorBgContainer, colorBorder },
  } = theme.useToken();
  const tableContainerRef = useRef();
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const createRef = useRef(null);
  const editRef = useRef(null);
  const viewRef = useRef(null);
  const { groupId, companyId } = useOutletContext();
  const { token } = useSelector((state) => state.auth);
  const [tradingPartnerForDisplay, setTradingPartnerForDisplay] = useState([]);
  const [tradingPartner, setTradingPartner] = useState([]);
  const [originalTradingPartnerForDisplay, setOriginalTradingPartnerForDisplay] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  
  // Search form state
  const [searchPayload, setSearchPayload] = useState({
    groupName: [],
    companyCode: [],
    tin: []
  });

  // Open the Drawer in the Create component
  const showChildDrawer = () => {
    if (createRef.current) {
      createRef.current.openDrawer(); // Call the openDrawer method in Create
    }
  };

  useEffect(() => {
    getTradingPartnerByGroupId();
  }, [groupId, companyId]);

  // Handle ResizeObserver errors
  useEffect(() => {
    const originalConsoleError = console.error;
    
    // Override console.error to suppress ResizeObserver errors
    console.error = (...args) => {
      const message = args[0]?.toString() || '';
      if (message.includes('ResizeObserver loop completed with undelivered notifications')) {
        return; // Suppress this specific error
      }
      originalConsoleError.apply(console, args); // Log other errors normally
    };

    const handleError = (e) => {
      if (e.message && e.message.includes('ResizeObserver loop completed with undelivered notifications')) {
        e.stopImmediatePropagation();
        e.preventDefault();
        return false;
      }
    };

    const handleUnhandledRejection = (e) => {
      if (e.reason && e.reason.message && e.reason.message.includes('ResizeObserver loop completed with undelivered notifications')) {
        e.preventDefault();
        return false;
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      console.error = originalConsoleError; // Restore original console.error
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Refresh the table
  const refreshTable = () => {
    // Placeholder for actual data refresh logic
    getTradingPartnerByGroupId();
  };

  // Handle form search
  const handleFormSearch = (payload = searchPayload) => {
    // Support calling as onClick handler (event object passed)
    const criteria = (payload && payload.target !== undefined) ? searchPayload : (payload || searchPayload);
    const groupNames = Array.isArray(criteria.groupName) ? criteria.groupName : [];
    const companyCodes = Array.isArray(criteria.companyCode) ? criteria.companyCode : [];
    const tins = Array.isArray(criteria.tin) ? criteria.tin : [];

    let filteredData = [...originalTradingPartnerForDisplay];

    // Apply group name filter
    if (groupNames.length > 0) {
      filteredData = filteredData.filter(record => {
        const recordValue = record.groupName ? record.groupName.toString().toLowerCase() : "";
        return groupNames.some(searchTerm => recordValue.includes((searchTerm || '').toString().toLowerCase()));
      });
    }

    // Apply Company Code filter
    if (companyCodes.length > 0) {
      filteredData = filteredData.filter(record => {
        const recordValue = record.companyCode ? record.companyCode.toString().toLowerCase() : "";
        return companyCodes.some(searchTerm => recordValue.includes((searchTerm || '').toString().toLowerCase()));
      });
    }

    // Apply TIN filter
    if (tins.length > 0) {
      filteredData = filteredData.filter(record => {
        const recordValue = record.tin ? record.tin.toString().toLowerCase() : "";
        return tins.some(searchTerm => recordValue.includes((searchTerm || '').toString().toLowerCase()));
      });
    }

    setTradingPartnerForDisplay(filteredData);
  };

  // Handle form clear
  const handleFormClear = () => {
    setSearchPayload({
      groupName: [],
      companyCode: [],
      tin: []
    });
    // Reset to original data
    setTradingPartnerForDisplay(originalTradingPartnerForDisplay);
  };

  const getTradingPartnerByGroupId = async () => {
    if (
      localStorage.getItem("groupId") !== null &&
      localStorage.getItem("companyId") !== null
    ) {
      await TradingPartnerAPI.getTradingPartnerByGroupId(
        token,
        localStorage.getItem("groupId"),
        localStorage.getItem("companyId")
      )
        .then((res) => {
          if (res?.data) {
            const rows = [];
            res.data.map((rowValue) => {
              let statusValue = null;
              if (rowValue.status) {
                statusValue = "Active";
              } else {
                statusValue = "Inactive";
              }
              rows.push({
                key: rowValue._id, // Add unique key for row selection
                _id: rowValue._id,
                groupName: rowValue.tradingPartnerName,
                companyCode: rowValue.companyCode ?? "",
                country: rowValue.country.name.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
                registrationValue: rowValue.registrationValue,
                tin: rowValue.tin,
                status: statusValue,
                emailAddress: rowValue.emailAddress,
              });
            });
            setTradingPartnerForDisplay(rows);
            setOriginalTradingPartnerForDisplay(rows);
            setTradingPartner(res.data);
          }
        })
        .catch((err) => {
          console.log("error getting user data :", err.message);
        });
    } else if (
      localStorage.getItem("groupId") !== null &&
      localStorage.getItem("companyId") === null
    ) {
      setTradingPartnerForDisplay();
      setTradingPartner();
    }
  };


  const initialColumns = [
    {
      title: "Trading Partner Name",
      dataIndex: "groupName",
      key: "groupName",
      sorter: (a, b) => a.groupName.localeCompare(b.groupName),
      percentageWidth: 0.24,
    },
    {
      title: "Company Code",
      dataIndex: "companyCode",
      key: "companyCode",
      sorter: (a, b) => (a.companyCode || "").localeCompare(b.companyCode || ""),
      percentageWidth: 0.12,
      render: (text) => text || "—",
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      sorter: (a, b) => a.country.localeCompare(b.country),
      percentageWidth: 0.16,
    },
    {
      title: "Email",
      dataIndex: "emailAddress",
      key: "emailAddress",
      sorter: (a, b) => (a.emailAddress || "").localeCompare(b.emailAddress || ""),
    },
    {
      title: "TIN",
      dataIndex: "tin",
      key: "tin",
      sorter: (a, b) => (a.tin || "").localeCompare(b.tin || ""),
      percentageWidth: 0.10,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      percentageWidth: 0.20,
      render: (status) => (
        <Badge
          status={status === "Active" ? "success" : "error"}
          text={status}
        />
      ),
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
    {
      title: "Action",
      key: "action",
      percentageWidth: 0.10,
      align: "center",
      render: (_, record) => (
        <Space
          size="small"
          style={{ display: "flex", justifyContent: "space-around" }}
        >
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={(e) => onViewChange(record._id)}
          />
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={(e) => onEditChange(record._id)}
          />
        </Space>
      ),
    },
  ]

  // Compute an initial container width using window.innerWidth as fallback.
  const initialContainerWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;

  // Set initial column widths based on the fallback container width.
  const [columns, setColumns] = useState(
    initialColumns.map(col => ({
      ...col,
      width: Math.floor(initialContainerWidth * col.percentageWidth),
    }))
  );

  // Function to update column widths based on the measured container.
  const updateColumnWidths = useCallback(() => {
    if (tableContainerRef.current) {
      const containerWidth = tableContainerRef.current.offsetWidth;
      setColumns(initialColumns.map(col => ({
        ...col,
        width: Math.floor(containerWidth * col.percentageWidth),
      })));
    }
  }, [initialColumns]);

  // Use useLayoutEffect to update column widths as soon as the component is mounted.
  useLayoutEffect(() => {
    updateColumnWidths();
  }, [updateColumnWidths]);

  // Update widths on window resize.
  useEffect(() => {
    window.addEventListener('resize', updateColumnWidths);
    return () => window.removeEventListener('resize', updateColumnWidths);
  }, [updateColumnWidths]);

  const onEditChange = (id) => {
    if (editRef.current) {
      editRef.id = id;
      editRef.current.openDrawer(); // Call the openDrawer method in Edit
    }
  };

  const onViewChange = (id) => {
    if (viewRef.current) {
      viewRef.id = id;
      viewRef.current.openDrawer(); // Call the openDrawer method in Edit
    }
  };

  // Handle selection change
  const handleSelectChange = (selectedKeys, selectedRows) => {
    setSelectedRows(selectedRows);
    setSelectedRowKeys(selectedKeys);
  };

  // Export to Excel functionality
  const handleExportToExcel = () => {
    if (selectedRows.length === 0) {
      notification.error({
        message: "No Trading Partners Selected",
        description: "Please select trading partners to export.",
        placement: "bottomLeft"
      });
      return;
    }

    try {
      // Get the selected trading partners' IDs
      const selectedIds = selectedRows.map(row => row._id);
      
      // Find the full data for selected trading partners from the original API response
      const selectedFullData = tradingPartner.filter(tp => selectedIds.includes(tp._id));

      // Prepare comprehensive data for Excel export with all available fields
      const headings = [
        "Company Code",
        "Branch",
        "Name", 
        "TIN Number",
        "ID Type",
        "ID Number",
        "MSIC Code",
        "Business Activity Description",
        "Address Line 1",
        "Address Line 2", 
        "Address Line 3",
        "Postal Code",
        "Address City Name",
        "Address State Code",
        "Address Country Code",
        "SST Registration Number",
        "TTX Registration Number",
        "Email Address",
        "Contact Number",
        "Default Classification Code",
        "Status",  
        "Created User",
        "Created Date",
        "Updated Date"
      ];

      const data = selectedFullData.map((rowValue) => [
        rowValue.companyCode || "",
        rowValue.isBranch ? "TRUE" : "FALSE",
        rowValue.tradingPartnerName || "",
        rowValue.tin || "",
        rowValue.registrationType || "",
        rowValue.registrationValue || "",
        rowValue.msicCode?.code || "",
        rowValue.msicCode?.description || "",
        rowValue.address?.addressLine1 || "",
        rowValue.address?.addressLine2 || "",
        rowValue.address?.addressLine3 || "",
        rowValue.address?.postalZone || "",
        rowValue.address?.city || "",
        rowValue.address?.state?.code || "",
        rowValue.country?.code || "",
        rowValue.sst || "",
        rowValue.ttx || "",
        rowValue.emailAddress || "",
        rowValue.contactNumber || "",
        rowValue.classificationCode || "",
        rowValue.status ? "Active" : "Inactive",
        rowValue.createdUserName || "",
        rowValue.created_at ? new Date(rowValue.created_at).toLocaleDateString() : "",
        rowValue.updated_at ? new Date(rowValue.updated_at).toLocaleDateString() : ""
      ]);

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([headings, ...data]);

      // Set column widths for better readability
      ws['!cols'] = [
        { wch: 25 }, // Trading Partner ID
        { wch: 35 }, // Trading Partner Name
        { wch: 18 }, // TIN
        { wch: 25 }, // Registration Type
        { wch: 20 }, // Registration Value
        { wch: 10 }, // SST
        { wch: 10 }, // TTX
        { wch: 12 }, // Status
        { wch: 35 }, // Email Address
        { wch: 18 }, // Contact Number
        { wch: 20 }, // Classification Code
        { wch: 15 }, // Company Code
        { wch: 20 }, // Country Name
        { wch: 12 }, // Country Code
        { wch: 50 }, // Address Line 1
        { wch: 20 }, // City
        { wch: 30 }, // State Name
        { wch: 12 }, // State Code
        { wch: 15 }, // MSIC Code
        { wch: 40 }, // MSIC Description
        { wch: 25 }, // Group ID
        { wch: 25 }, // Created User ID
        { wch: 15 }, // Created Date
        { wch: 15 }  // Updated Date
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Trading Partners Details");

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `Trading_Partners_Complete_${timestamp}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);

      notification.success({
        message: "Export Successful",
        description: `${selectedRows.length} trading partners with complete details exported successfully.`,
        placement: "bottomLeft"
      });
    } catch (error) {
      console.error("Export error:", error);
      notification.error({
        message: "Export Failed",
        description: "An error occurred while exporting the data.",
        placement: "bottomLeft"
      });
    }
  };

  const handleResize = useCallback(
    (index) => (e, { size }) => {
      setColumns((prevColumns) => {
        const newColumns = [...prevColumns];
        newColumns[index] = { ...newColumns[index], width: size.width };
        return newColumns;
      });
    },
    []
  );

  const mergedColumns = useMemo(
    () =>
      columns.map((col, index) => ({
        ...col,
        onHeaderCell: (column) => ({
          width: column.width,
          onResize: handleResize(index),
        }),
      })),
    [columns, handleResize]
  );

  return (
    <>
      <style>
        {`
          .custom-select-dropdown .ant-select-item-option-selected .ant-select-item-option-state {
            display: none !important;
          }
          .custom-select-dropdown .ant-select-item-option-selected::after {
            display: none !important;
          }
          .custom-select-dropdown .ant-select-item-option-selected .anticon-check {
            display: none !important;
          }
        `}
      </style>
      <Row>
        <Col md={24}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <Breadcrumb.Item href="/dashboard">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Master Data</Breadcrumb.Item>
          <Breadcrumb.Item>Trading Partners</Breadcrumb.Item>
        </Breadcrumb>
        <div className="border-container">
          <Button
            type="primary"
            disabled={
              localStorage.getItem("groupId") === null ||
              localStorage.getItem("companyId") === null
            }
            variant="dashed"
            className="card-button"
            size="large"
            icon={<PlusOutlined />}
            onClick={showChildDrawer}
            shape="circle"
          ></Button>
          <Card title="Trading Partners" bordered={true}>
            {selectedRows.length > 0 && (
              <Alert
                message={`${selectedRows.length} Trading Partners Selected (Click to Clear)`}
                type="info"
                showIcon
                style={{ 
                  width: 'fit-content', 
                  marginBottom: '16px', 
                  cursor: 'pointer'
                }}
                onClick={() => {
                  setSelectedRowKeys([]);
                  setSelectedRows([]);
                }}
              />
            )}
            
            <div style={{ marginBottom: '16px' }}>
              <Space>
                <Button
                  type="primary"
                  icon={<FileExcelOutlined />}
                  onClick={handleExportToExcel}
                  disabled={selectedRows.length === 0}
                >
                  Export to Excel
                </Button>
              </Space>
            </div>

            {/* Search Form */}
            <div style={{ 
              background: colorBgContainer, 
              padding: '24px', 
              borderRadius: '8px', 
              marginBottom: '16px',
              border: `1px solid ${colorBorder}`
            }}>
              <Form layout="vertical">
                <Row gutter={[12, 16]} align="bottom">
                  <Col lg={7} md={12} sm={24} xs={24}>
                    <Form.Item label="Trading Partner Name" style={{ marginBottom: '8px' }}>
                      <Select
                        mode="tags"
                        size="default"
                        value={searchPayload.groupName}
                        onChange={(values) => {
                          const updatedPayload = { ...searchPayload, groupName: values };
                          setSearchPayload(updatedPayload);
                          // Auto-filter when values change
                          setTimeout(() => handleFormSearch(updatedPayload), 0);
                        }}
                        placeholder="Enter Trading Partner Names"
                        style={{ width: '100%' }}
                        tokenSeparators={[',']}
                        allowClear
                        maxTagCount={false}
                        popupClassName="custom-select-dropdown"
                        optionRender={(option) => (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              width: '100%',
                            }}
                          >
                            <span>{option.label}</span>
                            {searchPayload.groupName.includes(option.value) && (
                              <span
                                style={{
                                  color: '#ff4d4f',
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                  cursor: 'pointer',
                                  padding: '2px 4px',
                                  borderRadius: '2px',
                                  backgroundColor: '#fff2f0',
                                  border: '1px solid #ffccc7',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const updatedPayload = { 
                                    ...searchPayload, 
                                    groupName: searchPayload.groupName.filter(item => item !== option.value) 
                                  };
                                  setSearchPayload(updatedPayload);
                                  setTimeout(() => handleFormSearch(updatedPayload), 0);
                                }}
                                title="Remove from selection"
                              >
                                ×
                              </span>
                            )}
                          </div>
                        )}
                        tagRender={(props) => {
                          const { label, closable, onClose } = props;
                          return (
                            <span
                              style={{
                                background: '#e6f7ff',
                                border: '1px solid #91d5ff',
                                borderRadius: '6px',
                                padding: '2px 8px',
                                margin: '2px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: '#1890ff',
                                fontSize: '12px',
                              }}
                            >
                              {label}
                              {closable && (
                                <span
                                  style={{
                                    marginLeft: '4px',
                                    cursor: 'pointer',
                                    color: '#999',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onClose();
                                    // Auto-filter after removing tag with updated payload
                                    const updatedPayload = { ...searchPayload, groupName: searchPayload.groupName.filter(item => item !== label) };
                                    setTimeout(() => handleFormSearch(updatedPayload), 0);
                                  }}
                                >
                                  ×
                                </span>
                              )}
                            </span>
                          );
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col lg={7} md={12} sm={24} xs={24}>
                    <Form.Item label="Company Code" style={{ marginBottom: '8px' }}>
                      <Select
                        mode="tags"
                        size="default"
                        value={searchPayload.companyCode}
                        onChange={(values) => {
                          const updatedPayload = { ...searchPayload, companyCode: values };
                          setSearchPayload(updatedPayload);
                          setTimeout(() => handleFormSearch(updatedPayload), 0);
                        }}
                        placeholder="Enter Company Codes"
                        style={{ width: '100%' }}
                        tokenSeparators={[',']}
                        allowClear
                        maxTagCount={false}
                        popupClassName="custom-select-dropdown"
                        optionRender={(option) => (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              width: '100%',
                            }}
                          >
                            <span>{option.label}</span>
                            {searchPayload.companyCode.includes(option.value) && (
                              <span
                                style={{
                                  color: '#ff4d4f',
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                  cursor: 'pointer',
                                  padding: '2px 4px',
                                  borderRadius: '2px',
                                  backgroundColor: '#fff2f0',
                                  border: '1px solid #ffccc7',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const updatedPayload = {
                                    ...searchPayload,
                                    companyCode: searchPayload.companyCode.filter(item => item !== option.value)
                                  };
                                  setSearchPayload(updatedPayload);
                                  setTimeout(() => handleFormSearch(updatedPayload), 0);
                                }}
                                title="Remove from selection"
                              >
                                ×
                              </span>
                            )}
                          </div>
                        )}
                        tagRender={(props) => {
                          const { label, closable, onClose } = props;
                          return (
                            <span
                              style={{
                                background: '#f6ffed',
                                border: '1px solid #b7eb8f',
                                borderRadius: '6px',
                                padding: '2px 8px',
                                margin: '2px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: '#389e0d',
                                fontSize: '12px',
                              }}
                            >
                              {label}
                              {closable && (
                                <span
                                  style={{
                                    marginLeft: '4px',
                                    cursor: 'pointer',
                                    color: '#999',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onClose();
                                    const updatedPayload = { ...searchPayload, companyCode: searchPayload.companyCode.filter(item => item !== label) };
                                    setTimeout(() => handleFormSearch(updatedPayload), 0);
                                  }}
                                >
                                  ×
                                </span>
                              )}
                            </span>
                          );
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col lg={7} md={12} sm={24} xs={24}>
                    <Form.Item label="TIN Number" style={{ marginBottom: '8px' }}>
                      <Select
                        mode="tags"
                        size="default"
                        value={searchPayload.tin}
                        onChange={(values) => {
                          const updatedPayload = { ...searchPayload, tin: values };
                          setSearchPayload(updatedPayload);
                          // Auto-filter when values change
                          setTimeout(() => handleFormSearch(updatedPayload), 0);
                        }}
                        placeholder="Enter TIN Numbers"
                        style={{ width: '100%' }}
                        tokenSeparators={[',']}
                        allowClear
                        maxTagCount={false}
                        popupClassName="custom-select-dropdown"
                        optionRender={(option) => (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              width: '100%',
                            }}
                          >
                            <span>{option.label}</span>
                            {searchPayload.tin.includes(option.value) && (
                              <span
                                style={{
                                  color: '#ff4d4f',
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                  cursor: 'pointer',
                                  padding: '2px 4px',
                                  borderRadius: '2px',
                                  backgroundColor: '#fff2f0',
                                  border: '1px solid #ffccc7',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const updatedPayload = { 
                                    ...searchPayload, 
                                    tin: searchPayload.tin.filter(item => item !== option.value) 
                                  };
                                  setSearchPayload(updatedPayload);
                                  setTimeout(() => handleFormSearch(updatedPayload), 0);
                                }}
                                title="Remove from selection"
                              >
                                ×
                              </span>
                            )}
                          </div>
                        )}
                        tagRender={(props) => {
                          const { label, closable, onClose } = props;
                          return (
                            <span
                              style={{
                                background: '#fff7e6',
                                border: '1px solid #ffd591',
                                borderRadius: '6px',
                                padding: '2px 8px',
                                margin: '2px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: '#d46b08',
                                fontSize: '12px',
                              }}
                            >
                              {label}
                              {closable && (
                                <span
                                  style={{
                                    marginLeft: '4px',
                                    cursor: 'pointer',
                                    color: '#999',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onClose();
                                    // Auto-filter after removing tag with updated payload
                                    const updatedPayload = { ...searchPayload, tin: searchPayload.tin.filter(item => item !== label) };
                                    setTimeout(() => handleFormSearch(updatedPayload), 0);
                                  }}
                                >
                                  ×
                                </span>
                              )}
                            </span>
                          );
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col lg={3} md={12} sm={24} xs={24}>
                    <Form.Item style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-end', height: '100%' }}>
                      <Space style={{ width: '100%', justifyContent: 'center' }}>
                        <Button size="default" onClick={handleFormSearch} type="primary">
                          Search
                        </Button>
                        <Button size="default" onClick={handleFormClear}>
                          Clear
                        </Button>
                      </Space>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </div>

            <div
              style={{
                maxWidth: '100%', // Ensure the container doesn't exceed its width
                overflowX: 'auto', // Enable horizontal scroll
              }}
            >
              <Table
                size="middle"
                components={{
                  header: {
                    cell: ResizableTitle,
                  },
                }}
                columns={mergedColumns}
                dataSource={tradingPartnerForDisplay}
                locale={{ emptyText: <Empty description='No records found!' /> }}
                rowSelection={{
                  selectedRowKeys,
                  onChange: handleSelectChange,
                }}
              />
            </div>
          </Card>
        </div>
        <Create ref={createRef} onRefresh={refreshTable} />
        <Edit ref={editRef} onRefresh={refreshTable} />
        <View ref={viewRef} onRefresh={refreshTable} />
      </Col>
    </Row>
    </>
  );
}
